import { Button, MagellanPopup } from 'components/atoms';
import { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PiWarning, PiXCircle } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { ARTIST_ROLE } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface IPopupCancelWarningProps {
  trigger?: JSX.Element;
  disabledTrigger?: boolean;
  name?: string;
  onCancel?: (actions: PopupActions) => void;
  cancelLoading?: boolean;
  transI18nKey?: string;
  isClear?: boolean;
  unsupportedRoles?: number[];
  artistsWithoutExternalIds?: Array<{ id: string; nickname: string }>;
  onOpen?: () => void | Promise<void>;
}

const PopupCancelWarning = ({
  trigger,
  disabledTrigger,
  name = '',
  onCancel,
  cancelLoading,
  transI18nKey,
  isClear = true,
  unsupportedRoles = [],
  artistsWithoutExternalIds = [],
  onOpen,
}: IPopupCancelWarningProps) => {
  const { t } = useTranslation();
  const popupRef = useRef<PopupActions>(null);

  const getRoleName = (roleNumber: number): string => {
    const roleKey = Object.keys(ARTIST_ROLE).find(
      key => ARTIST_ROLE[key as keyof typeof ARTIST_ROLE] === roleNumber
    );
    return roleKey ? t(`enum.ARTIST_ROLE.${roleKey}`) : `${roleNumber}`;
  };
  return (
    <Popup
      ref={popupRef}
      modal
      nested
      onOpen={onOpen}
      trigger={
        trigger ?? (
          <Button
            isClear={isClear}
            onlyIcon
            variant={BUTTON_VARIANT.warning}
            disabled={disabledTrigger}>
            <Button.Description description={t('button.cancel')} />
            <Button.Icon icon={<PiXCircle />} />
          </Button>
        )
      }>
      <MagellanPopup>
        <MagellanPopup.Header className='justify-center'>
          <PiWarning />
          {t(`popup.header`)}
        </MagellanPopup.Header>
        <div className='flex flex-col opacity-55 max-w-96 text-center gap-4'>
          <p>
            <Trans
              i18nKey={transI18nKey ?? 'popup.warning-popup'}
              values={{ name: name }}
              components={{ strong: <strong /> }}
            />
          </p>
          {unsupportedRoles.length > 0 && (
            <div className='bg-red-50 rounded-lg p-3 mt-4'>
              <p className='text-red-800 font-medium mb-2'>
                {t('music-product-digital.delivery.unsupported-roles-warning')}
              </p>
              <p className='text-red-700 text-sm'>
                {t('music-product-digital.delivery.unsupported-roles-list')}: {unsupportedRoles.map(role => getRoleName(role)).join(', ')}
              </p>
            </div>
          )}
          {artistsWithoutExternalIds.length > 0 && (
            <div className='bg-yellow-50 rounded-lg p-3 mt-4 mb-4'>
              <p className='text-yellow-800 font-medium mb-2'>
                {t('music-product-digital.delivery.artists-without-external-ids-warning')}
              </p>
              <p className='text-yellow-700 text-sm'>
                {t('music-product-digital.delivery.artists-without-external-ids-list')}: {artistsWithoutExternalIds.map(artist => artist.nickname || 'Unknown Artist').join(', ')}
              </p>
              <p className='text-yellow-600 text-xs mt-2'>
                {t('music-product-digital.delivery.artists-without-external-ids-info')}
              </p>
            </div>
          )}
          <p>{t(`popup.question`)}</p>
        </div>
        <div className='flex gap-2 mt-4'>
          <Button isClear onClick={() => popupRef.current?.close()}>
            <Button.Text text={t(`popup.cancel`)} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.warning}
            onClick={() =>
              onCancel && popupRef.current && onCancel(popupRef.current)
            }
            isLoading={cancelLoading}>
            <Button.Text text={t(`popup.approve`)} />
          </Button>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default PopupCancelWarning;
